import React from 'react';
import { useMessageDetail } from '../hooks/useOutreachHistory';

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-600',
    queued: 'bg-yellow-100 text-yellow-800',
    sending: 'bg-blue-100 text-blue-800',
    sent: 'bg-green-100 text-green-800',
    delivered: 'bg-green-100 text-green-800',
    opened: 'bg-purple-100 text-purple-800',
    replied: 'bg-indigo-100 text-indigo-800',
    bounced: 'bg-red-100 text-red-800',
    failed: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-600',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[status] ?? 'bg-gray-100 text-gray-600'}`}
    >
      {status}
    </span>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface ThreadViewModalProps {
  messageId: string;
  onClose: () => void;
}

export default function ThreadViewModal({ messageId, onClose }: ThreadViewModalProps) {
  const { data, isLoading, isError } = useMessageDetail(messageId);

  function handleBackdropClick(e: React.MouseEvent) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-900">Email Thread</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {isLoading && (
            <p className="text-sm text-gray-500 text-center py-8">Loading thread...</p>
          )}
          {isError && (
            <p className="text-sm text-red-600 text-center py-8">Failed to load message.</p>
          )}
          {data && (
            <>
              {/* Original message */}
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {data.message.subject ?? '(no subject)'}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      To: {data.message.toEmail}
                      {(data.message.contactFirstName || data.message.contactLastName) && (
                        <span className="text-gray-400 ml-1">
                          ({data.message.contactFirstName} {data.message.contactLastName})
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <StatusBadge status={data.message.status} />
                  </div>
                </div>

                {/* Sent time and open info */}
                <div className="flex items-center gap-4 text-xs text-gray-400">
                  {data.message.sentAt && (
                    <span>Sent {formatDate(data.message.sentAt)}</span>
                  )}
                  {data.message.openCount > 0 && (
                    <span className="flex items-center gap-1 text-purple-600">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Opened {data.message.openCount}×
                      {data.message.openedAt && <span className="ml-1">({formatDate(data.message.openedAt)})</span>}
                    </span>
                  )}
                </div>

                {/* Body */}
                {data.message.bodyHtml ? (
                  <div
                    className="text-sm text-gray-700 border border-gray-100 rounded-lg p-4 bg-gray-50 max-h-60 overflow-y-auto prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: data.message.bodyHtml }}
                  />
                ) : data.message.bodyText ? (
                  <pre className="text-sm text-gray-700 border border-gray-100 rounded-lg p-4 bg-gray-50 whitespace-pre-wrap max-h-60 overflow-y-auto font-sans">
                    {data.message.bodyText}
                  </pre>
                ) : null}
              </div>

              {/* Reply */}
              {data.message.replyReceivedAt && (
                <div className="border-l-4 border-indigo-200 pl-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-indigo-700">Reply received</span>
                    <span className="text-xs text-gray-400">{formatDate(data.message.replyReceivedAt)}</span>
                  </div>
                  {data.message.replyFromEmail && (
                    <p className="text-xs text-gray-500">From: {data.message.replyFromEmail}</p>
                  )}
                  {data.message.replySubject && (
                    <p className="text-xs font-medium text-gray-700">{data.message.replySubject}</p>
                  )}
                  {data.message.replyBodyText && (
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans bg-indigo-50 rounded-lg p-3 max-h-48 overflow-y-auto">
                      {data.message.replyBodyText}
                    </pre>
                  )}
                </div>
              )}

              {/* Failure info */}
              {data.message.failureReason && (
                <div className="rounded-lg bg-red-50 border border-red-100 p-3">
                  <p className="text-xs font-medium text-red-700 mb-1">Send failed</p>
                  <p className="text-xs text-red-600">{data.message.failureReason}</p>
                  {data.message.retryCount > 0 && (
                    <p className="text-xs text-red-400 mt-1">Retried {data.message.retryCount}×</p>
                  )}
                </div>
              )}

              {/* Other thread messages */}
              {data.thread.filter((m) => m.id !== data.message.id).length > 0 && (
                <div className="pt-4 border-t border-gray-100">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    Other messages in thread
                  </h3>
                  <div className="space-y-2">
                    {data.thread
                      .filter((m) => m.id !== data.message.id)
                      .map((msg) => (
                        <div
                          key={msg.id}
                          className="flex items-center justify-between text-xs py-2 px-3 bg-gray-50 rounded-lg"
                        >
                          <span className="text-gray-600 truncate">{msg.subject ?? '(no subject)'}</span>
                          <div className="flex items-center gap-2 shrink-0 ml-2">
                            {msg.sentAt && <span className="text-gray-400">{formatDate(msg.sentAt)}</span>}
                            <StatusBadge status={msg.status} />
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
